import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { StatusPill } from '@lyearn/ui';
import { CardContentType } from '@/modules/content/card/types';
import { getAdaptedBaseCardContent } from '@/modules/content/card/utils/getAdaptedBaseCardContent';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import WideCard from '@/modules/content/components/ContentCard/Wide';
import { WideCardContent } from '@/modules/content/components/ContentCard/Wide/renderers';
import { renderProviderLabel } from '@/modules/content/modules/external-content/utils';
import getAdaptedWideCardContent from '@/modules/content/utils/getAdaptedWideCardContent';
import { PerformanceStatusEnum, ResourceTypeEnum } from '@/types/schema';

interface NewWideCardProps extends Omit<BaseCard.BaseCardProps, 'content'> {
  // No need of height because we compute height from aspect ratio
  content: CardContentType;
  imageProps: { width: number; height: number };
  footer?: React.ReactNode;
  label?: React.ReactNode;
  accessories?: React.ReactNode;
  isNewHomepage?: boolean;
  actions?: React.ReactNode;
  renderInfo?: () => React.ReactNode;

  //survey
  isEmbed?: boolean;
}

function NewWideCard(props: NewWideCardProps) {
  const {
    disabled,
    imageProps,
    renderInfo,
    content: contentProp,
    onClick,
    isEmbed,
    className,
  } = props;
  const { t } = useTranslation();
  const content = getAdaptedBaseCardContent(t, contentProp);

  const _renderInfo =
    renderInfo ??
    (() => {
      if (content.contentType === ResourceTypeEnum.Survey) {
        if (!isEmbed) {
          return null;
        }

        const isCompleted = contentProp.performance?.status === PerformanceStatusEnum.Completed;

        return (
          <>
            <BaseCard.PrimaryAction className="mt-24 self-start">
              {t('Common:ContentCards.TakeSurvey')}
            </BaseCard.PrimaryAction>
            {isCompleted ? (
              <StatusPill
                appearance="secondary"
                className="absolute top-20 end-20"
                label={t('Common:ContentCards.Submitted')}
                variant="light"
              />
            ) : null}
          </>
        );
      }

      if (content.contentType === ResourceTypeEnum.ExternalContent) {
        return renderProviderLabel(props.content.provider);
      }
    });

  return (
    <WideCard
      className={className}
      content={getAdaptedWideCardContent(content)}
      disabled={disabled}
      onClick={onClick}>
      <WideCardContent imageProps={imageProps} renderInfo={_renderInfo} />
    </WideCard>
  );
}

export default NewWideCard;
